import React, { useCallback, useMemo } from 'react';
import { Paper } from '@mui/material';
import CloseRounded from '@mui/icons-material/CloseRounded';
import { hydrateArtifactByRef } from '../copilot/hooks/useArtifactHydration';
import { ChatStoreProvider, type ApprovalAction, type ChatStoreHook } from '../../chat/core';
import type { WorkspaceArtifact } from '../copilot/stores/useCopilotStore';
import { ChatPanelV2 } from '../../chat/panel';
import { ToolbarButton, type ContextChip } from '../../ui-kit';
import { dashboardChat, dashboardChatForWidgets } from './dashboardChat';

/**
 * Copilot, docked to the right of a dashboard.
 *
 * The panel is handed this dashboard's own chat, so it never sees another
 * one. Unlike the board's panel there is no fork to a legacy panel: this
 * surface has no conversations from before, so a second code path would be
 * dead the day it shipped.
 *
 * The widgets a question is about are shown as chips in the composer, each
 * removable. Adding a widget changes what an unqualified question means, so
 * it has to be visible and undoable — otherwise "why did this drop?" gets
 * answered about something the reader is not looking at, with nothing on
 * screen to explain why.
 */
export default function DashboardChatPanel({
  useStore,
  dashboardName,
  onSend,
  onStop,
  onSQLApproval,
  onAddToDashboard,
  onClose,
  contextChips = [],
  onRemoveContextChip,
}: {
  useStore: ChatStoreHook;
  dashboardName: string;
  onSend: (message: string) => void;
  onStop: () => void;
  onSQLApproval: (action: ApprovalAction, editedSql?: string, reason?: string) => void;
  /** Pin a result onto the dashboard this chat belongs to. */
  onAddToDashboard?: (artifact: WorkspaceArtifact) => void;
  onClose: () => void;
  /** The widgets this chat has been pointed at. */
  contextChips?: ContextChip[];
  /** Take a widget back out of the question. */
  onRemoveContextChip?: (key: string) => void;
}) {
  // The chips already say which widgets are in the question, so the openers
  // follow them: about the dashboard when none are added, about those widgets
  // when some are.
  const place = useMemo(
    () => (contextChips.length ? dashboardChatForWidgets(contextChips.map((c) => c.label)) : dashboardChat),
    [contextChips],
  );

  const handleArtifactHydrate = useCallback(
    (artifactId: string) => {
      void hydrateArtifactByRef(artifactId, useStore.getState());
    },
    [useStore],
  );

  return (
    <Paper
      variant="outlined"
      sx={(t) => ({
        height: '100%',
        minWidth: 0,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        borderRadius: `${t.ds.radius.md}px`,
        overflow: 'hidden',
      })}
    >
      <ChatStoreProvider store={useStore}>
        <ChatPanelV2
          place={place}
          title={dashboardName}
          subtitle="Answers from this dashboard"
          onSend={onSend}
          onStop={onStop}
          onSQLApproval={onSQLApproval}
          onAddToDashboard={onAddToDashboard}
          onArtifactHydrate={handleArtifactHydrate}
          contextChips={{ base: 'This dashboard', chips: contextChips, onRemove: onRemoveContextChip }}
          headerActions={
            <ToolbarButton
              variant="ghost"
              icon={<CloseRounded fontSize="small" />}
              onClick={onClose}
              aria-label="Close chat"
              tooltip="Close chat"
            />
          }
        />
      </ChatStoreProvider>
    </Paper>
  );
}
